/**
 * Lógica de los filtros del catálogo. Pura y sin React a propósito: es lo único
 * de esta feature que puede estar mal de forma silenciosa, así que se testea sola.
 *
 * La jerarquía es **categoría → género**, como la pidió la dueña el 2026-09-01:
 * "seccionar en Diseñador/Árabe/Nicho, y de ahí que partan a Hombre/Mujer/Unisex".
 * Invierte el pedido anterior (2026-08-31, que era género primero) — la categoría es
 * el eje principal y los géneros disponibles se calculan **dentro** de la categoría
 * elegida. Si elige Árabe y solo tiene árabes de hombre, no se le ofrece "Mujer".
 *
 * Las opciones se derivan SIEMPRE de los productos que hay: una categoría sin
 * productos no se pinta, así que Árabe y Nicho aparecen solos el día que suba el
 * primero de cada una. Cero mantenimiento.
 *
 * Lo que NO se hace es esconder una fila por tener una sola opción. Se intentó, y la
 * dueña preguntó dos veces por sus divisiones porque no las veía: una fila que
 * desaparece sola es indistinguible de una función que no se hizo. Hoy "Diseñador"
 * es la única categoría y aun así se muestra.
 */

/** Orden canónico. Se muestra en este orden, no en el que aparezcan en la base. */
export const ORDEN_GENERO = ['Hombre', 'Mujer', 'Unisex'];
export const ORDEN_CATEGORIA = ['Diseñador', 'Nicho', 'Árabe'];

const presentes = (valores, orden) => {
  const hay = new Set(valores.filter(Boolean));
  // Primero los conocidos en su orden; después cualquier valor inesperado de la
  // base, para que un dato raro se vea en vez de desaparecer sin avisar.
  const conocidos = orden.filter((v) => hay.has(v));
  const otros = [...hay].filter((v) => !orden.includes(v)).sort();
  return [...conocidos, ...otros];
};

/** Categorías con al menos un producto. Es el eje PRINCIPAL: se mira todo el catálogo. */
export function opcionesCategoria(productos) {
  return presentes(productos.map((p) => p.category), ORDEN_CATEGORIA);
}

/** Géneros con al menos un producto **dentro de la categoría elegida**. */
export function opcionesGenero(productos, categoria) {
  const base = categoria ? productos.filter((p) => p.category === categoria) : productos;
  return presentes(base.map((p) => p.gender), ORDEN_GENERO);
}

/**
 * Texto comparable: sin acentos, minúsculas, sin espacios de sobra.
 *
 * Los acentos importan de verdad acá: el catálogo tiene "Man Eau Fraîche" y "Árabe".
 * Sin normalizar, quien escribe "fraiche" desde el teclado del celular no encuentra
 * nada y concluye que el buscador está roto.
 */
const plano = (t) =>
  (t || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Busca por texto libre sobre marca, nombre y notas.
 *
 * Se piden TODAS las palabras, en cualquier orden y en cualquier campo: "dior parfum"
 * encuentra "Dior — Sauvage Eau de Parfum" aunque las dos palabras estén separadas.
 * Con substring simple esa búsqueda no daría nada, que es justo la que alguien escribe.
 */
export function buscar(productos, texto) {
  const palabras = plano(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return productos;
  return productos.filter((p) => {
    const heno = plano(`${p.brand} ${p.name} ${p.note}`);
    return palabras.every((w) => heno.includes(w));
  });
}

/**
 * Aplica los filtros. `''`/null = sin filtrar.
 *
 * Un producto sin categoría (`null`) sale en "Todas" pero no en ninguna categoría
 * concreta: no se le inventa una.
 */
/** true si algún producto tiene precio tachado, o sea si hay algo que ofertar. */
export function hayOfertas(productos) {
  return productos.some((p) => p.compareAtCents != null);
}

export function filtrar(productos, { genero = '', categoria = '', q = '', ofertas = false } = {}) {
  const base = productos.filter(
    (p) =>
      (!genero || p.gender === genero) &&
      (!categoria || p.category === categoria) &&
      (!ofertas || p.compareAtCents != null)
  );
  return buscar(base, q);
}

/**
 * Normaliza lo que venga en la URL contra lo que realmente existe.
 *
 * La URL es editable por cualquiera y además puede quedar vieja (un link de
 * WhatsApp de hace un mes a "Mujer › Árabe" cuando ya no hay árabes de mujer).
 * Sin esto el visitante abre el link y ve una grilla vacía sin entender por qué.
 */
export function sanear(productos, { genero = '', categoria = '', ofertas = false } = {}) {
  // La categoría se valida primero porque es el eje principal; el género, contra lo
  // que existe DENTRO de esa categoría.
  const c = opcionesCategoria(productos).includes(categoria) ? categoria : '';
  const g = opcionesGenero(productos, c).includes(genero) ? genero : '';
  // `?ofertas=1` sin ninguna oferta cargada mostraría una grilla vacía a quien abre
  // un link viejo. Se descarta, igual que un género que ya no existe.
  return { genero: g, categoria: c, ofertas: Boolean(ofertas) && hayOfertas(productos) };
}
